// src/pages/cadastros/Empresas/NovaEmpresa.jsx
// (Mamute Fitness) UI rebatizado para "Clientes".
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import { useAuth } from "../../../contexts/AuthContext";
import GoogleMapsAutocomplete from "../../../components/GoogleMapsAutocomplete/GoogleMapsAutocomplete";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";

async function obterProximoCodigoCliente(grupoId) {
  const contadorRef = doc(db, "contadores", `clientes_${grupoId}`);

  const proximo = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(contadorRef);

    let atual = 1;
    if (snap.exists() && snap.data().ultimoCodigoSequencial) {
      atual = snap.data().ultimoCodigoSequencial;
    }

    const novoValor = atual + 1;

    transaction.set(
      contadorRef,
      { ultimoCodigoSequencial: novoValor, grupoId },
      { merge: true }
    );

    return novoValor;
  });

  return proximo;
}

function NovaEmpresa() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();

  const grupoId = usuarioLogado?.grupoId || "";

  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [tipo, setTipo] = useState("Propria");

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setErro("");

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Não foi possível identificar o grupo do usuário logado. Verifique o cadastro do usuário."
      );
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome do cliente.");
      return;
    }

    setCarregando(true);

    try {
      const grupoId = usuarioLogado.grupoId;
      const grupoNome = usuarioLogado.grupoNome || grupoId;

      const sequencial = await obterProximoCodigoCliente(grupoId);
      const codigo = `CL${String(sequencial).padStart(4, "0")}`;

      const clientesRef = collection(db, "empresas");
      const novoRef = doc(clientesRef);

      await setDoc(novoRef, {
        // Campos do Cliente
        nome: nome.trim(),
        tipo: tipo || null,
        endereco: endereco.trim() || null,
        responsavel: responsavel.trim() || null,
        telefone: telefone.trim() || null,
        email: email.trim() || null,

        // Mantém a estrutura multi-tenant do Mamute
        grupoId,
        grupoNome,

        // Mantém código sequencial para identificação (útil em relatórios)
        codigoSequencial: sequencial,
        codigo,

        criadoPor: usuarioLogado.uid,
        criadoEm: serverTimestamp(),
      });

      alert(`Cliente criado com sucesso. Código: ${codigo}`);
      navigate("/cadastros/clientes");
    } catch (e) {
      console.error("Erro ao criar cliente:", e);
      setErro("Erro ao criar cliente. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Novo Cliente"
      subtitulo="Cadastros / Clientes / Novo cliente"
    >
      <section className="pc-card pc-form-card">
        <header className="pc-form-header">
          <h3>Cadastrar novo cliente</h3>
          <p>Informe os dados básicos do cliente. O grupo será assumido a partir do usuário logado.</p>
        </header>

        <form onSubmit={handleSubmit} className="pc-form">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          <div className="pc-form-grid">
            <div className="pc-form-group pc-form-group-full">
              <label htmlFor="nome">Nome *</label>
              <input
                id="nome"
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="pc-input"
                placeholder="Ex: Academia XYZ"
              />
            </div>

            <div className="pc-form-group">
              <label htmlFor="responsavel">Responsável</label>
              <input
                id="responsavel"
                type="text"
                value={responsavel}
                onChange={(e) => setResponsavel(e.target.value)}
                className="pc-input"
                placeholder="Ex: João Silva"
              />
            </div>

            <div className="pc-form-group">
              <label htmlFor="telefone">Telefone</label>
              <input
                id="telefone"
                type="text"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                className="pc-input"
                placeholder="Ex: (11) 99999-9999"
              />
            </div>

            <div className="pc-form-group pc-form-group-full">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pc-input"
                placeholder="Ex: contato@academia.com.br"
              />
            </div>

            <div className="pc-form-group pc-form-group-full">
              <label htmlFor="endereco">Endereço</label>
              <GoogleMapsAutocomplete
                value={endereco}
                onChange={setEndereco}
                onPlaceSelected={(place) => setEndereco(place.address || "")}
                placeholder="Rua, número, bairro, cidade..."
                className="pc-input"
                grupoId={grupoId}
                disabled={!grupoId}
              />
            </div>


            <div className="pc-form-group">
              <label htmlFor="tipo">Tipo</label>
              <select
                id="tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="pc-input"
              >
                <option value="Rede">Rede</option>
                <option value="Propria">Propria</option>
                <option value="CT">CT</option>
                <option value="Condominio">Condominio</option>
                <option value="Clinica">Clinica</option>
                <option value="Studio">Studio</option>
                <option value="Marca">Marca</option>
              </select>
            </div>

          </div>

          <div className="pc-form-actions">
            <button
              type="button"
              className="pc-btn-secundario"
              onClick={() => navigate("/cadastros/clientes")}
              disabled={carregando}
            >
              Voltar
            </button>
            <button
              type="submit"
              className="pc-btn-primario"
              disabled={carregando}
            >
              {carregando ? "Salvando..." : "Salvar cliente"}
            </button>
          </div>
        </form>
      </section>
    </LayoutDesktop>
  );
}

export default NovaEmpresa;
